import { type MatchStatus, matchStatusSchema, type Variant } from "@app/protocol";
import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE } from "../config";
import { useStore } from "../store";
import { openTable } from "../ws/singleton";
import { silence } from "./matchSilence";

/** The client asks again this often while it searches; the server forgets a silent searcher (10 s). */
const POLL_MS = 2_000;

export type Search = Extract<MatchStatus, { status: "searching" }> & {
  skew: number;
  /** The server has not answered for a while; the search goes on, the panel says so. */
  unreachable: boolean;
};

async function ask(session: string, method: "POST" | "DELETE", body?: unknown) {
  try {
    const response = await fetch(`${API_BASE}/match`, {
      method,
      headers: { Authorization: `Bearer ${session}`, "Content-Type": "application/json" },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (!response.ok) return null;
    const parsed = matchStatusSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * "Find players" (review 0020, ADR 0035): joins the game's queue, keeps the place by asking every
 * two seconds, and moves to the table once the server found one; the table seats the player.
 */
export function useMatch(): {
  search: Search | null;
  start: (variant: Variant) => void;
  stop: () => void;
} {
  const session = useStore((s) => s.session);
  const [search, setSearch] = useState<Search | null>(null);
  const searching = useRef<Variant | null>(null);

  const unanswered = useRef(0);

  const take = useCallback((status: MatchStatus | null) => {
    if (searching.current === null) return;
    const quiet = silence(unanswered.current, status !== null);
    unanswered.current = quiet.asks;
    if (status === null) {
      setSearch((current) =>
        current === null ? null : { ...current, unreachable: quiet.unreachable },
      );
      return;
    }
    if (status.status === "matched") {
      searching.current = null;
      setSearch(null);
      openTable(status.tableId);
    } else if (status.status === "searching")
      setSearch({ ...status, skew: status.now - Date.now(), unreachable: false });
    else {
      searching.current = null;
      setSearch(null);
    }
  }, []);

  const start = useCallback(
    (variant: Variant) => {
      if (session === null) return;
      searching.current = variant;
      unanswered.current = 0;
      setSearch({
        status: "searching",
        variant,
        since: Date.now(),
        now: Date.now(),
        waiting: 1,
        skew: 0,
        unreachable: false,
      });
      void ask(session, "POST", { variant, fresh: true }).then(take);
    },
    [session, take],
  );

  const stop = useCallback(() => {
    if (session === null || searching.current === null) return;
    searching.current = null;
    setSearch(null);
    void ask(session, "DELETE");
  }, [session]);

  const variant = search?.variant ?? null;
  useEffect(() => {
    if (session === null || variant === null) return;
    const id = window.setInterval(() => {
      void ask(session, "POST", { variant, fresh: false }).then(take);
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [session, variant, take]);

  // Leaving the home screen while still searching leaves the queue.
  useEffect(() => stop, [stop]);

  return { search, start, stop };
}
